import { t } from 'i18next';
import { ConfigurationInterfaceIpv4PortForward } from 'models/configuration-interface-ipv4-port-forward';
import { ControllerConfigurationThirdPartyFirewall } from 'models/controller-configuration-third-party-firewall';
import { ControllerConfigurationThirdPartyStaticRoute } from 'models/controller-configuration-third-party-static-route';
import { emailsStringFormat, ipToInt } from './helper';

const Netmask = require('netmask').Netmask;
/* NOTE: Validator 宣告成 function 的使用方式是為了解決部份 components 中在切換語系時無法即使吃到切換後的語系包問題 */
const Validator = () => {
  return {
    required: { value: true, message: t('validator.required') },
    minorName: {
      // \u0800-\u4e00 (日文) \u4e00-\u9fa5 (中文)
      value: /^[0-9a-zA-Z\u0800-\u9fa5~!@#$%^&*()_\-+=[\]{}|;':"<\\>?,./\s]*$/,
      message: t('validator.minorName'),
    },
    checkMinorNameLength(name: string, min: number, max: number) {
      const length = encodeURIComponent(name).replace(/%[A-F\d]{2}/g, 'U').length;
      if (length > max || length < min) {
        return `${t('validator.minMaxLength')} ${min} ~ ${max} ${t('validator.characters')} ${t('validator.chineseJapaneseTip')}`;
      }
      return true;
    },
    namePassword: {
      value: /^[0-9a-zA-Z~!@#$%^&*()_\-+=[\]{}|;':"<\\>?,./\s]*$/,
      message: t('validator.namePassword'),
    },
    userPassword: {
      value: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[$@$!%*#?&])[A-Za-z\d$@$!%*#?&]{8,63}$/,
      message: t('validator.userPassword'),
    },
    captivePortalNamePassword: {
      value: /^[0-9a-zA-Z~!@#$%^&*()_\-+=[\]{}|;':"<\\>?,./\s]{8,32}$/,
      message: t('validator.captivePortalNamePassword'),
    },
    email: {
      value: /^\w+((-\w+)|(\.\w+)|(\+\w+))*@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z]+$/,
      message: t('validator.email'),
    },
    remoteKey: {
      value: /^[A-Za-z0-9]{8,63}$/,
      message: t('validator.remoteKey'),
    },
    ipv4Ip: {
      value:
        /^((25[0-5]|2[0-4][0-9]|[1][0-9]{2}|[1-9][0-9]|[1-9])\.((25[0-5]|2[0-4][0-9]|[1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.){2}(25[0-5]|2[0-4][0-9]|[1][0-9]{2}|[1-9][0-9]|[0-9]))$/,
      message: t('validator.ipv4Ip'),
    },
    ipv6Ip: {
      value:
        /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/,
      message: t('validator.ipv6Ip'),
    },
    /* NOTE: 針對 MAC 欄位增加第二層驗證
     *   Deny zeor MAC address 00:00:00:00:00:00
     *   Deny broadcast MAC address FF:FF:FF:FF:FF:FF
     *   Deny multicast MAC address(第 2 碼為: 1, 3, 5, 7, 9, B, D, F)
     *	   [x1:xx:xx:xx:xx:xx:xx, 01:00:5E:xx:xx:xx IPv4]
     *	   [x3:xx:xx:xx:xx:xx:xx, 33:33:xx:xx:xx:xx IPv6]
     *	   [x5:xx:xx:xx:xx:xx:xx]
     *	   [x7:xx:xx:xx:xx:xx:xx]
     *	   [x9:xx:xx:xx:xx:xx:xx]
     *	   [xB:xx:xx:xx:xx:xx:xx]
     *	   [xD:xx:xx:xx:xx:xx:xx]
     *	   [xF:xx:xx:xx:xx:xx:xx]
     */
    checkIsDeniedMacAddress(mac: string | undefined) {
      // NOTE: There may be no MAC information
      if (mac !== undefined) {
        const macAddress = mac.toUpperCase();

        // Deny Zero MAC Address
        if (macAddress === '00:00:00:00:00:00') return t('validator.checkIsDeniedZeroMacAddress');
        // Deny Broadcast MAC Address
        if (macAddress === 'FF:FF:FF:FF:FF:FF') return t('validator.checkIsDeniedBroadcastMacAddress');
        // Deny Multicast MAC Address ranges
        if (/^.[13579BDF]:/.test(macAddress)) return t('validator.checkIsDeniedMulticastMacAddress');
      }
      return true;
    },
    mac: {
      value: /^(([a-fA-F0-9]{2}:){5}([a-fA-F0-9]{2}))$/,
      message: t('validator.mac'),
    },
    ipDomain: {
      value:
        /^(([A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,6})$|^((25[0-5]|2[0-4][0-9]|[1][0-9]{2}|[1-9][0-9]|[1-9])\.((25[0-5]|2[0-4][0-9]|[1][0-9]{2}|[1-9][0-9]|[1-9]|0)\.){2}(25[0-5]|2[0-4][0-9]|[1][0-9]{2}|[1-9][0-9]|[0-9]))$/,
      message: t('validator.ipDomain'),
    },
    ipUrl: {
      value: /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/,
      message: t('validator.ipUrl'),
    },
    domain: {
      value: /^([\da-z\.-]+)\.([a-z\.]{2,6})$/,
      message: t('validator.domain'),
    },
    min(min: number, max: number) {
      return { value: min, message: `${t('validator.minMaxLength')} ${min} ~ ${max}.` };
    },
    max(min: number, max: number) {
      return { value: max, message: `${t('validator.minMaxLength')} ${min} ~ ${max}.` };
    },
    minLength(min: number, max: number) {
      return { value: min, message: `${t('validator.minMaxLength')} ${min} ~ ${max} ${t('validator.characters')}` };
    },
    maxLength(min: number, max: number) {
      return { value: max, message: `${t('validator.minMaxLength')} ${min} ~ ${max} ${t('validator.characters')}` };
    },
    emailDuplicate(value: string) {
      const emails = emailsStringFormat(value);
      const map = new Map();
      for (const email of emails) {
        if (map.has(email)) {
          return t('validator.emailDuplicate', {
            email,
          });
        } else {
          map.set(email, true);
        }
      }
      return true;
    },
    checkEmailFormat(value: string, pattern: RegExp) {
      const emails = emailsStringFormat(value);
      const reg = new RegExp(pattern);
      for (const email of emails) {
        if (!reg.test(email)) {
          return t('validator.emailFormatIncorrect', {
            email,
          });
        }
      }
      return true;
    },
    rangePort(value: string) {
      const regex = /^[0-9]{1,5}(-[0-9]{1,5})?$/;
      if (regex.test(value)) {
        const strArray = value.split('-');
        const start = parseInt(strArray[0]);
        const end = parseInt(strArray[1]);
        if (strArray.length === 2) {
          return (start < end && start >= 1 && end >= 1 && start <= 65535 && end <= 65535) || t('validator.rangePortStartPort');
        } else {
          return (start >= 1 && start <= 65535) || t('validator.rangePort');
        }
      } else {
        return t('validator.rangePortInvalidPortFormat');
      }
    },
    duplicate(
      newName: string,
      editIndex: number = -1,
      array: ConfigurationInterfaceIpv4PortForward[] | ControllerConfigurationThirdPartyStaticRoute[] | ControllerConfigurationThirdPartyFirewall[]
    ) {
      const isSame = array.some((a, index: number) => {
        if (editIndex >= 0) {
          return a.name === newName && index !== editIndex;
        } else {
          return a.name === newName;
        }
      });
      return !isSame || `${t('validator.duplicate')} ${newName}`;
    },
    wifiNameDuplicate(
      newName: string,
      nameList: { band2G: string[]; band5G: string[]; band6G: string[] },
      locationSearch: string,
      oldName: string | undefined,
      currentBand: string[],
      parameter: string
    ) {
      return locationSearch === '?new'
        ? (currentBand.includes('2G') && nameList.band2G?.includes(newName)) ||
          (currentBand.includes('5G') && nameList.band5G?.includes(newName)) ||
          (currentBand.includes('6G') && nameList.band6G?.includes(newName))
          ? t('validator.existedDuplicate', { parameter })
          : true
        : (currentBand.includes('2G') && nameList.band2G?.includes(newName) && newName !== oldName) ||
          (currentBand.includes('5G') && nameList.band5G?.includes(newName) && newName !== oldName) ||
          (currentBand.includes('6G') && nameList.band6G?.includes(newName) && newName !== oldName)
        ? t('validator.existedDuplicate', { parameter })
        : true;
    },
    profileNameDuplicate(
      newProfileName: string,
      profileNameList: string[],
      locationSearch: string,
      oldProfileName: string | undefined,
      parameter: string
    ) {
      return locationSearch === '?new'
        ? profileNameList.includes(newProfileName)
          ? t('validator.existedDuplicate', { parameter })
          : true
        : profileNameList.includes(newProfileName) && newProfileName !== oldProfileName
        ? t('validator.existedDuplicate', { parameter })
        : true;
    },
    vidDuplicate(newVid: number, vidList: number[], locationSearch: string, oldVid: number | undefined) {
      return locationSearch === '?new'
        ? vidList.includes(newVid)
          ? t('validator.existedDuplicate', { parameter: t('settings.profileManagement.vlansTableVid') })
          : true
        : vidList.includes(newVid) && newVid !== oldVid
        ? t('validator.existedDuplicate', { parameter: t('settings.profileManagement.vlansTableVid') })
        : true;
    },
    ipv4InDHCP(ip: string, leaseFirst: number, rangeEnd: string) {
      const ipLast = parseInt(ip.split('.')[3]);
      const rangeEndLast = parseInt(rangeEnd.split('.')[3]);
      if (ipLast >= leaseFirst && ipLast <= rangeEndLast) {
        return false;
      }
      return true;
    },
    checkNumber(v: number) {
      return isNaN(v) ? t('validator.checkNumber') : true;
    },
    checkInteger(v: number) {
      return Math.floor(v) === v || t('validator.checkInteger');
    },
    checkNetworkName(checkDefault: boolean, v: string) {
      return checkDefault === false && v.toLowerCase() === 'default' ? t('validator.checkNetworkName') : true;
    },
    isIpv4SubnetMask(ip: string): boolean {
      const ipOctets: number[] = ip.split('.').map((v) => parseInt(v, 10));

      /* conver to binary string */
      const ipBinary: string = ipOctets.map((octet) => octet.toString(2).padStart(8, '0')).join('');

      const segment: string[] = ipBinary.split(/1+/);

      /* for valid subnet mask should be ["", "0000..."] (0 length could be 1 to 31) */
      if (segment[0] === '' && segment.length === 2) {
        return true;
      }

      return false;
    },
    denyIpv4SubnetMask(ip: string): string | boolean {
      if (this.isIpv4SubnetMask(ip)) {
        return t('validator.denyIpv4SubnetMask');
      }

      return true;
    },
    checkIpv4Deny(ip: string): string | boolean {
      const ipOctets: number[] = ip.split('.').map((v) => parseInt(v, 10));

      /* invalid ip, not match orginal string, such as ' 192.168.1.01' <=> [192, 168, 1, 1] */
      if (ipOctets.join('.') !== ip) {
        return t('validator.checkIpv4Deny');
      }

      /* cannot be loopback address: 127.x.x.x */
      if (ipOctets[0] === 127) {
        return t('validator.checkIpv4DenyCannotLoopback');
      }

      /* cannot be link-local address: 169.254.x.x */
      if (ipOctets[0] === 169 && ipOctets[1] === 254) {
        return t('validator.checkIpv4DenyCannotLinklocal');
      }

      /* cannot be multicast address: 224.x.x.x ~ 239.x.x.x */
      if (ipOctets[0] >= 224 && ipOctets[0] <= 239) {
        return t('validator.checkIpv4DenyCannotMulticast');
      }

      /* cannot be subnet address: x.x.x.0 */
      if (ipOctets[3] === 0) {
        return t('validator.checkIpv4DenyCannotSubnet');
      }

      /* cannot be broadcast address: x.x.x.255 */
      if (ipOctets[3] === 255) {
        return t('validator.checkIpv4DenyCannotBroadcast');
      }

      return true;
    },
    checkNotInSubnetMaskOfLAN(ip: string, subnet: string): string | boolean {
      //  Check IP whether not within LAN's subnet mask
      const ipParse: string = ip
        .split('.')
        .map((v) => parseInt(v, 10))
        .join('.');

      //  `block.contains()` from https://www.npmjs.com/package/netmask
      const block = new Netmask(subnet);
      if (block.contains(ipParse)) {
        return t('validator.checkNotInSubnetMaskOfLAN');
      }
      return true;
    },
    checkInSubnetMaskOfLAN(ip: string, subnet: string): string | boolean {
      //  Check IP whether within LAN's subnet mask
      const ipParse: string = ip
        .split('.')
        .map((v) => parseInt(v, 10))
        .join('.');

      //  `block.contains()` from https://www.npmjs.com/package/netmask
      const block = new Netmask(subnet);
      if (!block.contains(ipParse)) {
        return t('validator.checkInSubnetMaskOfLAN');
      }
      return true;
    },
    checkIsEqualToLanIp(ip: string, subnet: string): string | boolean {
      //  Check IP whether equal to LAN IP address
      const ipParse: string = ip
        .split('.')
        .map((v) => parseInt(v, 10))
        .join('.');
      const [LANIP] = subnet.split('/');

      if (ipParse === LANIP) {
        return t('validator.checkIsEqualToLanIp');
      }
      return true;
    },
    checkInSubnetMaskOfWAN(ip: string, subnet: string): string | boolean {
      //  Check IP whether within WAN's subnet mask
      const ipParse: string = ip
        .split('.')
        .map((v) => parseInt(v, 10))
        .join('.');

      //  `block.contains()` from https://www.npmjs.com/package/netmask
      const block = new Netmask(subnet); // ex: 192.168.1.1/24
      if (!block.contains(ipParse)) {
        return t('validator.checkInSubnetMaskOfWAN');
      }
      return true;
    },
    checkIpConflict(ipv4Address1: string, ipv4Address2: string) {
      const ipv4AddressBlock1 = new Netmask(ipv4Address1);
      const ipv4AddressBlock2 = new Netmask(ipv4Address2);
      // 檢查ipv4Address1與ipv4Address2是否衝突
      if (ipv4AddressBlock1.contains(ipv4AddressBlock2.first) || ipv4AddressBlock2.contains(ipv4AddressBlock1.first)) {
        return t('validator.checkIpConflict'); // 存在衝突
      }
      return true; // 不存在衝突
    },
    // NOTE: CIDR（無類別域間路由）表示法是一個 IP 地址和子網掩碼的組合表示法，如："192.168.1.1/24"
    checkNotInSubnetMaskListOfLan(ipAddressOfCidr: string, subnetList: string[]): string | boolean {
      for (const subnet of subnetList) {
        if (typeof this.checkIpConflict(ipAddressOfCidr, subnet) === 'string') return this.checkIpConflict(ipAddressOfCidr, subnet);
      }
      return true;
    },
    checkIsNetworkOrBroadcastAddress(ip: string, netmask: number): string | boolean {
      const block = new Netmask(`${ip}/${netmask}`); // ex: 192.168.1.1/24
      if (ip === block.base || ip === block.broadcast) {
        return t('validator.checkIsNetworkOrBroadcastAddress');
      }
      return true;
    },
    checkIsValidDhcpRange(ip: string, startIp: string, endIp: string): string | boolean {
      const longIp = ipToInt(ip);
      const longStartIp = ipToInt(startIp);
      const longEndIp = ipToInt(endIp);
      if (longIp < longStartIp || longIp > longEndIp) {
        return t('validator.invalidRange', { rangeStart: startIp, rangeEnd: endIp });
      }
      return true;
    },
    checkStartEnd(startIp: string, endIp: string, type: 'start' | 'end') {
      const rangeStartInt = ipToInt(startIp);
      const rangeEndInt = ipToInt(endIp);
      if (rangeStartInt > rangeEndInt) {
        return type === 'start' ? t('validator.startMoreThanEnd') : t('validator.endLessThanStart');
      }
      return true;
    },
    checkNameBeginEndWithSpace(name: string) {
      // 去除開頭空白後的字串
      const trimmedStart = name.replace(/^[\s\uFEFF\xA0]+/g, '');
      // 去除結尾空白後的字串
      const trimmedEnd = name.replace(/[\s\uFEFF\xA0]+$/g, '');
      // 判斷開頭空白
      if (name !== trimmedStart) {
        return t('validator.checkNameBeginWithSpace');
      }
      // 判斷結尾空白
      if (name !== trimmedEnd) {
        return t('validator.checkNameEndWithSpace');
      }
      return true; // 開頭跟結尾都沒有空白，回傳 true
    },
    checkFileSize(file: File, maxFileSize: number) {
      if (file && file.size > maxFileSize) {
        return t('validator.checkCaptivePortalFileSize', {
          fileSize: maxFileSize,
        });
      }
      return true;
    },
    checkSiteName: {
      value: /^(?=[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9]$).{5,32}$/,
      message: t('validator.checkSiteName'),
    },
    /**
     * @param newSiteName
     * @param siteNameList
     * @param oldSiteName
     * @param parameter
     * @returns {boolean | string}
     */
    siteNameDuplicated(newSiteName: string, siteNameList: string[], oldSiteName: string | null, parameter: string): boolean | string {
      const normalizedNewName = newSiteName.trim().toLowerCase();
      const normalizedOldName = oldSiteName?.trim().toLowerCase() || '';
      return siteNameList.some((name) => {
        const siteName = name.trim().toLowerCase();
        return siteName === normalizedNewName && siteName !== normalizedOldName;
      })
        ? t('validator.existedDuplicate', { parameter })
        : true;
    },
    checkExternalCustomParams: {
      value: /^([^\s"'=,]+=[^\s"'=,]+,)*[^\s"'=,]+=[^\s"'=,]+$/,
      message: `${t('settings.profileManagement.placeholderCaptivePortalRemoteCustomParams')} ${t('validator.checkExternalCustomParams')}`,
    },
  };
};

export default Validator;
